import Paper from '@mui/material/Paper'
import React from 'react'
import { Link, useLoaderData } from 'react-router-dom'
import { formatDateYYYYMMDD, formatDateYYYYMMDDhhmmss } from '../../../../packages/utils/numberFormatters'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { PressReleaseEntity } from '@finance-lab/utils/types'
import { getUrlForType } from '../../../../utils/getUrlForEntity'
import IssueTypeLabels, { HasWarrantLabel, KVLabel } from '../../../../components/entities/IssueTypeLabels'
import { getIssueTypeLabel } from '../../../../utils/issues/getIssueTypeLabel'
import { getIssueDate } from '../../../../utils/issues/getIssueDate'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import { getWarrantLabel } from '../../../../utils/warrants/getWarrantLabel'
import Box from '@mui/material/Box'

const getPublishDate = (document: Document) => {
    const raw = document.querySelector('DateAndTime')?.textContent
    const year = raw?.slice(0, 4)
    const month = raw?.slice(4, 6)
    const day = raw?.slice(6, 8)
    const hour = raw?.slice(9, 11)
    const minute = raw?.slice(11, 13)
    const second = raw?.slice(13, 15)
    const [, timeZoneOffset] = raw?.split('+') || []

    return `${year}-${month}-${day}T${hour}:${minute}:${second}+${timeZoneOffset}`
}

const PaperWrapper = ({ children }: { children: React.ReactNode }) => {
    return <Paper sx={{ padding: 4, maxWidth: 800, img: { maxWidth: '100%' } }}>{children}</Paper>
}

export const PressRelease = () => {
    const pressRelease = useLoaderData() as PressReleaseEntity
    const domParser = new DOMParser()
    const bodyXml = domParser.parseFromString(pressRelease?.body ?? '', 'application/xml')
    const body = bodyXml.querySelector('MimeType[FormalName="text/vnd.Millistream"]+DataContent body')

    if (!body) {
        return (
            <PaperWrapper>
                <h1>Någonting gick fel</h1>

                <p>Vi kunde tyvärr inte ladda pressmeddelandet.</p>
            </PaperWrapper>
        )
    }

    // Remove attachment from body (MFN, Bequoted) since we handle them separately
    body.querySelector('.mfn-attachment, .bequoted-files')?.remove()

    const exRef = bodyXml.querySelector('pubdata')?.getAttribute('ex-ref')
    const publishDate = getPublishDate(bodyXml)

    const attachments = Array.from(bodyXml.querySelectorAll('MimeType[FormalName="application/pdf"]'))
        .map((el) => ({
            href: el.parentElement?.getAttribute('Href'),
            caption:
                el.parentElement?.querySelector('Caption')?.textContent ??
                el.parentElement?.getAttribute('Href')?.split('/').pop(),
        }))
        .filter((att) => !!att.href) as Array<{ href: string; caption?: string }>

    return (
        <>
            <PaperWrapper>
                <h1>{pressRelease.title}</h1>

                <p>{formatDateYYYYMMDDhhmmss(publishDate)}</p>

                <div dangerouslySetInnerHTML={{ __html: body?.innerHTML }} />
                {attachments.length > 0 && (
                    <div>
                        <h3>Bifogade filer</h3>

                        <ul>
                            {attachments.map(({ caption, href }) => (
                                <li key={href}>
                                    <a href={href}>{caption || href}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <p>Ursprung: {exRef ? <a href={exRef}>{exRef}</a> : 'Ursprungslänk saknas'}</p>
            </PaperWrapper>
            <Grid container>
                <Grid size={{ xs: 6 }} alignItems='stretch'>
                    <Card sx={{ marginTop: 4 }}>
                        <CardContent>
                            <Typography variant='h6' sx={{ paddingBottom: 4 }}>
                                Relaterade transaktioner
                            </Typography>

                            <Table>
                                <TableBody>
                                    {pressRelease.relatedTransactions.map((t) => (
                                        <TableRow key={t.id}>
                                            {t.type === 'issue' && (
                                                <>
                                                    <TableCell>{formatDateYYYYMMDD(getIssueDate(t))}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ paddingRight: 2, display: 'inline-block' }}>
                                                            <IssueTypeLabels
                                                                issue={{
                                                                    ...t,
                                                                    type: t.issueType,
                                                                    hasWarrants: t.numWarrants ? 1 : 0,
                                                                }}
                                                            />
                                                        </Box>
                                                        <Link to={getUrlForType(t)}>
                                                            {getIssueTypeLabel(t.issueType, t.earnout)}
                                                            &nbsp;
                                                        </Link>
                                                    </TableCell>
                                                </>
                                            )}
                                            {t.type === 'convertible' && (
                                                <>
                                                    <TableCell>{formatDateYYYYMMDD(t.maturityDate)}</TableCell>
                                                    <TableCell>
                                                        <Link to={getUrlForType(t)}>
                                                            <KVLabel /> {t.name}
                                                        </Link>
                                                    </TableCell>
                                                </>
                                            )}
                                            {t.type === 'warrant' && (
                                                <>
                                                    <TableCell>{formatDateYYYYMMDD(t.subEndDate)}</TableCell>
                                                    <TableCell>
                                                        <Link to={getUrlForType(t)}>
                                                            <Box sx={{ paddingRight: 2, display: 'inline-block' }}>
                                                                <HasWarrantLabel />
                                                            </Box>
                                                            {getWarrantLabel(t)}
                                                        </Link>
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </>
    )
}
